import React, { useState, useEffect, useRef } from 'react';
import { formatBalance, balanceToneClass } from '../../../lib/helpers/balance';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { busquedaSuficiente, getImageCacheKey, getImageUrl, invalidateImageCache } from '../../../lib/helpers';
import { isValidEmail } from '../../../lib/email';
import { toIsoDateOrFallback, isIncompleteDate } from '../../../lib/dates';
import CustomModal from '../../Elements/CustomModal';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import { scanDni } from '../../../lib/dniScanner';
import { fusionarEscaneoDni } from '../../../lib/fusionarEscaneoDni';
import FirmarDocumento from './FirmarDocumento';
// ── Duration helpers ────────────────────────────────────────────────────────
// Antes vivían copiados aquí; con `days` habrían tenido que cambiar dos veces
// y quedarse desincronizados para siempre. La copia canónica es la de
// MembershipDateFields (la de EmployeeModal/OwnersModal, algo más defensiva:
// descarta presets de duración 0 y fechas no numéricas).
import {
  DEFAULT_MEMBERSHIP_DURATIONS as DEFAULT_DURATIONS,
  sumarDuracion,
  findMatchingDurationPresetId,
  toISO,
  toDisplay,
} from '../../Elements/MembershipDateFields';

// Un texto, dos puertas: la guarda local y el 422 `signature_required` del servidor.
const TEXTO_FIRMA_OBLIGATORIA = 'Este club exige el acta firmada para dar de alta: pide al socio que firme.';

/** Solo el «Email already registered» del backend es un duplicado: otro 400 que
 *  mencione «email» (formato, longitud…) se lee tal cual, no como «ya existe». */
const esEmailDuplicado = (texto) => /email already registered/i.test(String(texto || ''));

/** Alineado con backend: NIF, NIE, pasaporte extranjero (hasta 20 caracteres). */
function normalizeNationalIdInput(raw) {
  const s = String(raw ?? '').trim().toUpperCase().replace(/\s+/g, '');
  return s || null;
}

/** Caducidad un año después de la inscripción (ISO); el backend exige caducidad > inscripción. */
function defaultExpirationAfterEnrollment(enrollmentIso) {
  const [y, m, d] = enrollmentIso.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  end.setFullYear(end.getFullYear() + 1);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

function getClientProfileImagePath(profile) {
  return profile?.documents_path || profile?.image_path || null;
}

// ── DateField — reusable date text+picker combo ─────────────────────────────
function DateField({ label, value, onChange, pickerValue, onPickerChange, placeholder = 'dd/mm/aaaa' }) {
  return (
    <div className="wa-form-field">
      <label className="wa-label">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            let v = e.target.value.replace(/[^\d/]/g, '');
            if (v.length <= 10) {
              const nums = v.replace(/\//g, '');
              let fmt = '';
              for (let i = 0; i < nums.length; i++) {
                if (i === 2 || i === 4) fmt += '/';
                fmt += nums[i];
              }
              onChange(fmt);
              if (fmt.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(fmt)) {
                const [d, m, y] = fmt.split('/');
                onPickerChange(`${y}-${m}-${d}`);
              }
            }
          }}
          placeholder={placeholder}
          maxLength={10}
          className="wa-input pr-10"
        />
        <input
          type="date"
          value={pickerValue}
          onChange={(e) => {
            if (e.target.value) {
              const [y, m, d] = e.target.value.split('-');
              onChange(`${d}/${m}/${y}`);
              onPickerChange(e.target.value);
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-base" aria-hidden>📅</span>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
// Buscador de avalista (plan B avales): autocompleta contra /v1/clients/search
// y enlaza la FK. Min 2 letras + debounce 300ms + abort de la petición anterior:
// una consulta pequeña solo cuando el staff teclea, cero coste al abrir el modal.
function AvalistaField({ token, shopId, value, onChange, selected, onSelect }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const numeroDe = (r) => r.legacy_member_id || r.membership_id || '';
  const nombreDe = (r) => `${r.profile?.first_name || ''} ${r.profile?.last_name || ''}`.trim() || r.email || 'Socio';

  const search = (q) => {
    if (abortRef.current) abortRef.current.abort();
    if (!q || !busquedaSuficiente(q) || !shopId || !token) { setSugs([]); setOpen(false); return; }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    fetch(`/v1/clients/search/${shopId}?q=${encodeURIComponent(q.trim())}&limit=8`, {
      headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { setSugs(Array.isArray(rows) ? rows : []); setOpen(true); })
      .catch(() => { /* abort o red: sin sugerencias, sin ruido */ });
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    onSelect(null); // teclear invalida el enlace anterior
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  return (
    <div className="relative">
      <input
        value={value}
        onChange={handleChange}
        onFocus={() => { if (sugs.length) setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="nombre o nº de socio"
        className="wa-input"
      />
      {selected && <p className="text-[11px] text-emerald-600 mt-0.5">Avalista enlazado ✓</p>}
      {open && sugs.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
          {sugs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  onSelect({ id: r.id, numero: numeroDe(r) });
                  onChange(`${numeroDe(r)} — ${nombreDe(r)}`);
                  setOpen(false);
                }}
              >
                <span className="font-medium">Nº {numeroDe(r)}</span> — {nombreDe(r)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomerModal({ active, handleModal, token, id, clientData, prefillData, shopName, shopId, setErrorMessage, setShowNuevaEntradaModal, onClientCreated, onClientUpdated }) {
  const navigate = useNavigate();
  const isPreRegistrationFlow = !id && !!prefillData?.preRegistrationId;
  // Core fields
  const [email, setEmail]                             = useState('');
  const [image, setImage]                             = useState(null);
  const [signatureDataUrl, setSignatureDataUrl]       = useState(null);
  // Firma traída de la preinscripción (el socio la hizo en su móvil). No caduca
  // al cambiar datos: no está delante para volver a firmar (decidido 02-09).
  const [firmaDelMovil, setFirmaDelMovil]             = useState(false);
  const [firmaMovilError, setFirmaMovilError]         = useState(false);
  // El trazo exacto que vino del móvil, para distinguir "la han vuelto a firmar"
  // de "la han mirado y cerrado".
  const firmaMovilRef = useRef(null);
  const firmaMovilCancelada = useRef(false);
  // Firma del avalador, hecha aquí en la tablet al aprobar/dar de alta (03-09).
  // Siempre caduca al cambiar datos: el avalador está delante.
  const [firmaAvaladorDataUrl, setFirmaAvaladorDataUrl] = useState(null);
  const [nombreSocioInput, setNombreSocioInput]       = useState('');
  const [apellidoSocioInput, setApellidoSocioInput]   = useState('');
  const [dniSocioInput, setDniSocioInput]             = useState('');
  const [fechaNacimientoSocioInput, setFechaNacimientoSocioInput] = useState('');
  const [datePickerValue, setDatePickerValue]         = useState('');
  const [inscripcionInput, setInscripcionInput]       = useState('');
  const [datePickerInscripcion, setDatePickerInscripcion] = useState('');
  const [caducidadInput, setCaducidadInput]           = useState('');
  const [datePickerCaducidad, setDatePickerCaducidad] = useState('');
  const [telefonoSocioInput, setTelefonoSocioInput]   = useState('');   // E.164 (+34...) o ''
  const [direccionSocioInput, setDireccionSocioInput] = useState('');
  // Extra fields
  const [socioAvaladoInput, setSocioAvaladoInput]     = useState('');
  const [socioAvaladoSel, setSocioAvaladoSel]         = useState(null); // {id, numero} del avalista enlazado (FK)
  const [lineaCosteInput, setLineaCosteInput]         = useState('Defecto');
  const [descuentoInput, setDescuentoInput]           = useState('Sin descuento');
  const [cuotaInput, setCuotaInput]                   = useState('');
  const [consumoMaxInput, setConsumoMaxInput]         = useState('');
  const [consumptionTypeInput, setConsumptionTypeInput] = useState(null);
  const [cpSocioInput, setCpSocioInput]               = useState('');
  const [ciudadSocioInput, setCiudadSocioInput]       = useState('');
  const [notesInput, setNotesInput]                   = useState('');
  // Internal Nº socio: previewed next number (gap-fill) shown on create, editable as an override.
  const [membershipInput, setMembershipInput]         = useState('');
  const [nextMembershipId, setNextMembershipId]       = useState(null);
  // ¿Este club exige el acta firmada para dar de alta? (`prereg_signature_required`,
  // petición CRUO 11-09-2026). null = sin cargar: se trata como "no exige" para no
  // bloquear un alta por un fallo de red — el 422 del servidor es la red de verdad.
  const [firmaObligatoria, setFirmaObligatoria]       = useState(null);
  // UI state
  const [localError, setLocalError]   = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Cambios sin guardar. Son 1.600 líneas de formulario —foto, DNI por las dos
  // caras, acta y firma manuscrita— y hasta ahora Cancelar llamaba a
  // handleModal() sin preguntar: un Esc se llevaba todo por delante.
  const [isDirty, setIsDirty] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const markDirty = () => setIsDirty(true);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [sociosCap, setSociosCap]     = useState(null);
  // Cuota de inscripción cobrada automáticamente al alta (configurada por venue).
  const [inscriptionFee, setInscriptionFee] = useState(null);
  const [chargeInscriptionFee, setChargeInscriptionFee] = useState(true);
  // Importe de ESTA alta: pre-rellenado con la cuota por defecto del local, editable.
  const [feeAmountInput, setFeeAmountInput] = useState('');
  // Cuota de inscripción: siempre efectivo (va a la caja). Se mantiene el valor por si
  // el backend lo espera; ya no hay selector de tarjeta.
  // Método real de la cuota (plan CRUO §D). 'debt' = a deber, que es lo que el
  // backend hacía SIEMPRE aunque la pantalla dijera «Efectivo»: el default honra
  // el comportamiento de siempre, y ahora la etiqueta dice la verdad.
  const [feePaymentMethod, setFeePaymentMethod] = useState('debt');
  const [feeDtoTipo, setFeeDtoTipo] = useState('percent');
  const [feeDtoValor, setFeeDtoValor] = useState('');
  const [durations, setDurations]     = useState(DEFAULT_DURATIONS);
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  // Camera (member photo)
  const [cameraOpen, setCameraOpen]   = useState(false);
  const [cameraError, setCameraError] = useState('');
  // DNI scanner
  const [dniScanError, setDniScanError]     = useState('');
  const [dniScanning, setDniScanning]       = useState(false);
  const [dniPartial, setDniPartial]         = useState(null); // data collected from front
  const [dniScanFilled, setDniScanFilled]       = useState([]);   // campos que ha rellenado el escaneo
  const [dniScanConflicts, setDniScanConflicts] = useState([]);   // lo que propone y NO ha pisado
  const [dniFrontFile, setDniFrontFile]     = useState(null);
  const [dniBackFile, setDniBackFile]       = useState(null);
  const [dniCameraOpen, setDniCameraOpen]   = useState(false);
  const [dniCameraError, setDniCameraError] = useState('');
  const [dniCameraSide, setDniCameraSide]   = useState('front');
  // Pre-registration DNI previews (blob URLs, fetched with auth)
  const [dniPrefillFrontUrl, setDniPrefillFrontUrl] = useState(null);
  const [dniPrefillBackUrl, setDniPrefillBackUrl]   = useState(null);
  const [dniExistingFrontUrl, setDniExistingFrontUrl] = useState(null);
  const [dniExistingBackUrl, setDniExistingBackUrl]   = useState(null);
  const [dniNewFrontPreviewUrl, setDniNewFrontPreviewUrl] = useState(null);
  const [dniNewBackPreviewUrl, setDniNewBackPreviewUrl]   = useState(null);

  const lastLoadedIdRef  = useRef(null);
  const galleryInputRef  = useRef(null);
  const cameraInputRef   = useRef(null);
  const dniCamFrontInputRef  = useRef(null);
  const dniCamBackInputRef   = useRef(null);
  const dniGalleryFrontRef   = useRef(null);
  const dniGalleryBackRef    = useRef(null);
  const videoRef         = useRef(null);
  const canvasRef        = useRef(null);
  const mediaStreamRef   = useRef(null);
  const dniVideoRef      = useRef(null);
  const dniCanvasRef     = useRef(null);
  const dniMediaStreamRef = useRef(null);
  // Lo que escribió el escaneo, campo a campo: un escaneo posterior puede corregir
  // a otro (el reverso lleva dígitos de control), pero lo tecleado por una persona
  // no se sobrescribe nunca.
  const dniScanWrittenRef = useRef({});
  const dniScanDataRef    = useRef(null);

  // ── Camera helpers ─────────────────────────────────────────────────────
  const stopStream = (ref) => {
    if (ref.current) { ref.current.getTracks().forEach((t) => t.stop()); ref.current = null; }
  };

  // ── Fetch duration presets (crear y editar: botones 1m / 3m / 1a en edición) ──
  useEffect(() => {
    if (!active || !shopId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}/membership-durations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.length) setDurations(data);
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [active, shopId, token]);

  // ── Fetch venue: cupo de socios, cuota de inscripción y si exige el acta firmada ──
  // Un solo GET para todo lo que el alta necesita saber del club (el review cazó un
  // segundo GET a la misma URL para leer solo el flag de la firma).
  useEffect(() => {
    if (!active || id || !shopId || !token) { setSociosCap(null); setFirmaObligatoria(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/v1/venues/${shopId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && (data.max_socios != null || data.socios_count != null)) {
          setSociosCap({ socios_count: data.socios_count ?? 0, max_socios: data.max_socios ?? null });
        }
        if (!cancelled) {
          const fee = data.inscription_fee != null ? Number(data.inscription_fee) : null;
          setInscriptionFee(fee);
          setFeeAmountInput(fee != null ? String(fee) : '');
          setChargeInscriptionFee(fee != null && fee > 0);
          // `prereg_signature_required` (CRUO 11-09-2026): el mismo campo que pinta la
          // tarjeta de Configuración. Sin dato (fallo de red) no se bloquea nada: el
          // 422 del servidor es la red de verdad.
          setFirmaObligatoria(Boolean(data.prereg_signature_required));
        }
      } catch { if (!cancelled) setSociosCap(null); }
    })();
    return () => { cancelled = true; };
  }, [active, id, shopId, token]);

  // Preview the next internal Nº socio for the create form (editable override).
  // danimateu shows this on its create screen; we mirror it. It's a hint, not a
  // reservation — the real number is assigned at create time.
  useEffect(() => {
    if (!active || id || !shopId || !token) { setNextMembershipId(null); setMembershipInput(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/v1/clients/next-membership-id/${shopId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const next = res?.data?.next_membership_id;
        if (!cancelled && next != null) {
          setNextMembershipId(next);
          setMembershipInput(String(next));
        }
      } catch { /* preview is only a hint; ignore failures */ }
    })();
    return () => { cancelled = true; };
  }, [active, id, shopId, token]);

  // ── Load existing client data ──────────────────────────────────────────
  useEffect(() => {
    if (active && id && clientData) {
      if (lastLoadedIdRef.current === id) return;
      lastLoadedIdRef.current = id;

      const parseDate = (raw) => {
        if (!raw) return { display: '', iso: '' };
        const s = String(raw).split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
          const [y, m, d] = s.split('-');
          return { display: `${d}/${m}/${y}`, iso: s };
        }
        return { display: '', iso: '' };
      };

      setEmail(clientData.email || '');
      setNombreSocioInput(clientData.profile?.first_name || '');
      setApellidoSocioInput(clientData.profile?.last_name || '');
      setDniSocioInput(clientData.profile?.national_id?.toString() || '');

      const bd = parseDate(clientData.profile?.birth_date);
      setFechaNacimientoSocioInput(bd.display); setDatePickerValue(bd.iso);

      const enr = parseDate(clientData.profile?.enrollment_date);
      setInscripcionInput(enr.display); setDatePickerInscripcion(enr.iso);

      const exp = parseDate(clientData.profile?.expiration_date);
      setCaducidadInput(exp.display); setDatePickerCaducidad(exp.iso);

      const initialPhone = clientData.profile?.phone?.toString() || '';
      setTelefonoSocioInput(initialPhone);
      setDireccionSocioInput(clientData.profile?.address || '');
      setSocioAvaladoInput(clientData.profile?.guarantor_member || '');
      setSocioAvaladoSel(clientData.profile?.guarantor_user_id
        ? { id: clientData.profile.guarantor_user_id, numero: clientData.profile.guarantor_member || '' }
        : null);
      setLineaCosteInput(clientData.profile?.cost_line || 'Defecto');
      setDescuentoInput(clientData.profile?.discount || 'Sin descuento');
      {
        const raw = String(clientData.profile?.fee ?? '').trim();
        if (!raw) setCuotaInput('');
        else {
          const compact = raw.replace(/\s/g, '');
          const legacy = /^0[,.]00€?$/.test(compact) || raw === '0.00 €' || raw === '0,00 €';
          setCuotaInput(legacy ? '' : raw);
        }
      }
      setConsumoMaxInput(clientData.profile?.max_monthly_consumption?.toString() || '');
      const ludenos = clientData.profile?.consumption_type?.ludenos || false;
      const terap   = clientData.profile?.consumption_type?.terap   || false;
      setConsumptionTypeInput(ludenos ? 'ludenos' : terap ? 'terap' : null);
      // 0 = sin código postal: no se enseña como «0» en el campo.
      setCpSocioInput(clientData.profile?.postal_code ? String(clientData.profile.postal_code) : '');
      setCiudadSocioInput(clientData.profile?.city || '');
      setNotesInput(clientData.profile?.notes || '');
      const profileImagePath = getClientProfileImagePath(clientData.profile);
      if (profileImagePath) {
        setCurrentImageUrl(getImageUrl(profileImagePath, {
          cacheKey: getImageCacheKey('client', clientData.id || id),
        }));
      } else setCurrentImageUrl(null);
    } else if (!active) {
      lastLoadedIdRef.current = null;
    }

    if (active && !id) {
      // Reset create form
      setEmail(''); setImage(null);
      setNombreSocioInput(''); setApellidoSocioInput(''); setDniSocioInput('');
      // Ficha nueva: el escaneo del socio anterior no cuenta como «lo escribió un escaneo».
      dniScanWrittenRef.current = {}; dniScanDataRef.current = null;
      setDniScanFilled([]); setDniScanConflicts([]);
      setFechaNacimientoSocioInput(''); setDatePickerValue('');
      // Default inscription date to today so duration buttons work immediately on open
      const _today = new Date();
      setInscripcionInput(toDisplay(_today)); setDatePickerInscripcion(toISO(_today));
      setCaducidadInput(''); setDatePickerCaducidad('');
      setTelefonoSocioInput(''); setDireccionSocioInput('');
      // Se vacían AQUÍ, antes de la precarga: hacerlo después (como estaba) borraba la
      // ciudad y el CP que trae la preinscripción por QR desde el 22-09-2026.
      setCpSocioInput(''); setCiudadSocioInput('');
      setSocioAvaladoInput(''); setSocioAvaladoSel(null); setLineaCosteInput('Defecto');
      setDescuentoInput('Sin descuento'); setCuotaInput('');
      setDniFrontFile(null); setDniBackFile(null); setDniPartial(null);
      setSignatureDataUrl(null);
      setFirmaAvaladorDataUrl(null);
      setFirmaDelMovil(false);
      setFirmaMovilError(false);
      firmaMovilRef.current = null;
      firmaMovilCancelada.current = true;
      // Apply pre-fill data from pre-registration approval if provided
      if (prefillData) {
        // Si el socio ya firmó en su móvil al preinscribirse, la firma se trae y
        // no se le vuelve a pedir. Se carga como data URL porque es lo que manda
        // el alta. (ACTA_FIRMA_PLAN.md F3)
        if (prefillData.signatureUrl) {
          firmaMovilCancelada.current = false;
          (async () => {
            try {
              const res = await axios.get(prefillData.signatureUrl, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
              });
              const dataUrl = await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onload = () => resolve(fr.result);
                fr.onerror = reject;
                fr.readAsDataURL(res.data);
              });
              if (firmaMovilCancelada.current) return;
              firmaMovilRef.current = dataUrl;
              setSignatureDataUrl(dataUrl);
              setFirmaDelMovil(true);
            } catch {
              // Que falle NO es lo mismo que "no firmó": si se traga el error, el
              // staff da el alta sin acta mientras la firma sigue guardada. Se
              // avisa para que el socio firme aquí a sabiendas.
              if (firmaMovilCancelada.current) return;
              firmaMovilRef.current = null;
              setFirmaDelMovil(false);
              setFirmaMovilError(true);
            }
          })();
        }
        if (prefillData.nombre)    setNombreSocioInput(prefillData.nombre);
        if (prefillData.apellidos) setApellidoSocioInput(prefillData.apellidos);
        if (prefillData.dni)       setDniSocioInput(prefillData.dni);
        // Recortado a 20: `pre_registrations.phone` admite 50 y `profiles.phone` 20.
        // Sin esto se enseñaba un teléfono de 27 caracteres y se guardaba otro.
        if (prefillData.telefono) setTelefonoSocioInput(String(prefillData.telefono).slice(0, 20));
        if (prefillData.email)     setEmail(prefillData.email);
        if (prefillData.direccion) setDireccionSocioInput(prefillData.direccion);
        if (prefillData.ciudad)    setCiudadSocioInput(prefillData.ciudad);
        if (prefillData.cp)        setCpSocioInput(String(prefillData.cp));
        if (prefillData.fecha_nacimiento) {
          setFechaNacimientoSocioInput(prefillData.fecha_nacimiento);
          const [d, m, y] = prefillData.fecha_nacimiento.split('/');
          setDatePickerValue(`${y}-${m}-${d}`);
        }
        if (prefillData.caducidad) {
          setCaducidadInput(prefillData.caducidad);
          const [d, m, y] = prefillData.caducidad.split('/');
          setDatePickerCaducidad(`${y}-${m}-${d}`);
        }
      }
      setConsumoMaxInput(''); setConsumptionTypeInput(null);
      setNotesInput(prefillData?.notes || '');
      // photo_path is now an auth-protected proxy path; resolved via effect below
      setCurrentImageUrl(null); setSelectedDurationId(null);
    }
  }, [active, id, clientData, prefillData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pre-registration photo: fetch with auth token → blob URL ──────────────
  useEffect(() => {
    const path = prefillData?.photo_path;
    if (!active || id || !path || !token) return;

    // Absolute URL (old presigned S3) — use directly
    if (path.startsWith('http://') || path.startsWith('https://')) {
      setCurrentImageUrl(path);
      return;
    }

    // Proxy path — must fetch with auth header
    let blobUrl = null;
    let cancelled = false;
    const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const url = `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;

    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled || !blob.size) return;
        blobUrl = URL.createObjectURL(blob);
        setCurrentImageUrl(blobUrl);
      } catch { /* leave null */ }
    })();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [active, id, prefillData?.photo_path, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pre-registration DNI previews: fetch front + back with auth ──────────
  useEffect(() => {
    const makeFetcher = (path, setter) => {
      if (!active || id || !path || !token) { setter(null); return; }
      if (path.startsWith('http://') || path.startsWith('https://')) { setter(path); return; }
      let blobUrl = null;
      let cancelled = false;
      const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const url = `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
      (async () => {
        try {
          const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok || cancelled) return;
          const blob = await res.blob();
          if (cancelled || !blob.size) return;
          blobUrl = URL.createObjectURL(blob);
          setter(blobUrl);
        } catch { /* leave null */ }
      })();
      return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    };

    const cleanFront = makeFetcher(prefillData?.dni_front_path, setDniPrefillFrontUrl);
    const cleanBack  = makeFetcher(prefillData?.dni_back_path,  setDniPrefillBackUrl);
    return () => { cleanFront?.(); cleanBack?.(); };
  }, [active, id, prefillData?.dni_front_path, prefillData?.dni_back_path, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit mode: fetch existing DNI scans with auth ───────────────────────
  useEffect(() => {
    if (!active || !id || !token) {
      setDniExistingFrontUrl(null);
      setDniExistingBackUrl(null);
      return undefined;
    }

    let cancelled = false;
    const blobUrls = [];

    const fetchSide = async (side, setter) => {
      try {
        const res = await fetch(`/v1/clients/${id}/dni/${side}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !res.ok) {
          if (!cancelled) setter(null);
          return;
        }
        const blob = await res.blob();
        if (cancelled || !blob.size) {
          if (!cancelled) setter(null);
          return;
        }
        const blobUrl = URL.createObjectURL(blob);
        blobUrls.push(blobUrl);
        setter(blobUrl);
      } catch {
        if (!cancelled) setter(null);
      }
    };

    fetchSide('front', setDniExistingFrontUrl);
    fetchSide('back', setDniExistingBackUrl);

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [active, id, token]);

  useEffect(() => {
    if (!dniFrontFile) { setDniNewFrontPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(dniFrontFile);
    setDniNewFrontPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [dniFrontFile]);

  useEffect(() => {
    if (!dniBackFile) { setDniNewBackPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(dniBackFile);
    setDniNewBackPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [dniBackFile]);

  // ── Edición: marcar el botón de duración al abrir según fechas guardadas (y al cargar presets del venue) ──
  useEffect(() => {
    if (!active || !id || !clientData?.profile || !durations.length) return;
    const toDayIso = (raw) => {
      if (!raw) return '';
      const s = String(raw).split('T')[0];
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
    };
    const enroll = toDayIso(clientData.profile.enrollment_date);
    const expire = toDayIso(clientData.profile.expiration_date);
    setSelectedDurationId(findMatchingDurationPresetId(enroll, expire, durations));
  }, [active, id, clientData, durations]);

  // Stop camera streams on modal close
  useEffect(() => {
    if (!active) {
      stopStream(mediaStreamRef); setCameraOpen(false); setCameraError('');
      stopStream(dniMediaStreamRef); setDniCameraOpen(false); setDniCameraError('');
      setDniScanError(''); setDniPartial(null); setDniFrontFile(null); setDniBackFile(null);
      setSignatureDataUrl(null);
    }
  }, [active]);

  // ── Member photo camera ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const open = async () => {
      if (!cameraOpen) { stopStream(mediaStreamRef); return; }
      setCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('Tu navegador no permite acceder a la cámara directamente.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        mediaStreamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch { if (!cancelled) setCameraError('No se pudo acceder a la cámara. Revisa permisos.'); }
    };
    open();
    return () => { cancelled = true; stopStream(mediaStreamRef); };
  }, [cameraOpen]);

  // ── DNI camera ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const open = async () => {
      if (!dniCameraOpen) { stopStream(dniMediaStreamRef); return; }
      setDniCameraError('');
      if (!navigator.mediaDevices?.getUserMedia) {
        setDniCameraError('Tu navegador no permite acceder a la cámara directamente.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        dniMediaStreamRef.current = stream;
        if (dniVideoRef.current) {
          dniVideoRef.current.srcObject = stream;
          await dniVideoRef.current.play().catch(() => {});
        }
      } catch {
        if (!cancelled) {
          setDniCameraError('No se pudo acceder a la cámara trasera. Puedes usar el selector del dispositivo.');
        }
      }
    };
    open();
    return () => { cancelled = true; stopStream(dniMediaStreamRef); };
  }, [dniCameraOpen]);

  // ── DNI scan logic ─────────────────────────────────────────────────────
  // Campos que puede rellenar el escaneo: qué propone, qué hay ahora en el
  // formulario y cómo se escribe.
  const buildDniFields = (merged) => [
    { key: 'firstName',  label: 'Nombre',              text: merged.firstName,           current: nombreSocioInput,
      apply: () => setNombreSocioInput(merged.firstName) },
    { key: 'lastName',   label: 'Apellidos',           text: merged.lastName,            current: apellidoSocioInput,
      apply: () => setApellidoSocioInput(merged.lastName) },
    { key: 'dni',        label: 'DNI / NIE',           text: merged.dni,                 current: dniSocioInput,
      apply: () => setDniSocioInput(merged.dni) },
    { key: 'birthDate',  label: 'Fecha de nacimiento', text: merged.birthDate?.display,  current: fechaNacimientoSocioInput,
      apply: () => { setFechaNacimientoSocioInput(merged.birthDate.display); setDatePickerValue(merged.birthDate.iso); } },
    { key: 'expiryDate', label: 'Caducidad',           text: merged.expiryDate?.display, current: caducidadInput,
      apply: () => { setCaducidadInput(merged.expiryDate.display); setDatePickerCaducidad(merged.expiryDate.iso); } },
    { key: 'address',    label: 'Dirección',           text: merged.address,             current: direccionSocioInput,
      apply: () => setDireccionSocioInput(merged.address) },
    // La ciudad la lee el reverso (línea bajo «DOMICILIO»). El backend la mandaba
    // desde siempre y este formulario la tiraba; por eso «la ciudad del OCR» nunca
    // llegó a la ficha (CRUO, 21-09-2026).
    { key: 'city',       label: 'Ciudad',              text: merged.city,                current: ciudadSocioInput,
      apply: () => setCiudadSocioInput(merged.city) },
  ];

  const applyDniData = (data, existing = {}) => {
    if (!data) return;
    const merged = fusionarEscaneoDni(existing, data);
    dniScanDataRef.current = merged;
    const filled = [];
    const conflicts = [];
    buildDniFields(merged).forEach((field) => {
      if (!field.text) return;
      const current = (field.current || '').trim();
      // Un documento mal leído puede traer cualquier cosa: si en el campo hay algo
      // escrito por una persona, se propone, no se pisa.
      if (current && current !== dniScanWrittenRef.current[field.key]) {
        if (current !== field.text) conflicts.push({ key: field.key, label: field.label, text: field.text });
        return;
      }
      if (current === field.text) return;
      field.apply();
      dniScanWrittenRef.current[field.key] = field.text;
      filled.push(field.label);
    });
    setDniScanFilled(filled);
    setDniScanConflicts(conflicts);
  };

  // «Usar»: el escaneo solo escribe encima de lo tecleado si lo pide la persona.
  const applyDniSuggestion = (key) => {
    const merged = dniScanDataRef.current;
    if (!merged) return;
    const field = buildDniFields(merged).find((f) => f.key === key);
    if (!field || !field.text) return;
    field.apply();
    dniScanWrittenRef.current[key] = field.text;
    setDniScanConflicts((cs) => cs.filter((c) => c.key !== key));
    setDniScanFilled((f) => (f.includes(field.label) ? f : [...f, field.label]));
  };

  const processDniFile = async (file, side) => {
    if (!file) return;
    if (side === 'front') setDniFrontFile(file);
    if (side === 'back') setDniBackFile(file);
    if (id) return;
    setDniScanning(true); setDniScanError('');
    setDniScanFilled([]); setDniScanConflicts([]);
    const result = await scanDni(file, side, token);
    setDniScanning(false);
    if (result.data) {
      if (side === 'front') {
        // Store front data so back scan can merge on top of it
        setDniPartial(result.data);
        applyDniData(result.data, {});
      } else {
        applyDniData(result.data, dniPartial || {});
      }
    }
    if (!result.success) {
      // `timeout` es la señal interna del escáner, no un texto para la pantalla.
      setDniScanError(
        result.error === 'timeout'
          ? 'No se pudo leer el documento a tiempo. Rellena los datos a mano o prueba otra foto.'
          : (result.error || 'No se pudo leer la imagen.')
      );
    }
    else setDniScanError('');
  };

  const handleDniFrontCameraCapture = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    await processDniFile(file, 'front');
  };

  const handleDniBackCameraCapture = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    await processDniFile(file, 'back');
  };


  const openDniScanner = (step) => {
    setDniScanError('');
    setDniCameraSide(step);
    setDniCameraError('');
    setDniCameraOpen(true);
  };

  const handleDniCapturePhoto = () => {
    const video = dniVideoRef.current;
    const canvas = dniCanvasRef.current;
    if (!video || !canvas) {
      setDniCameraError('No se pudo capturar la imagen del DNI.');
      return;
    }
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setDniCameraError('No se pudo generar la imagen del DNI.');
        return;
      }
      const side = dniCameraSide === 'back' ? 'back' : 'front';
      const file = new File([blob], `dni-${side}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopStream(dniMediaStreamRef);
      setDniCameraOpen(false);
      await processDniFile(file, side);
    }, 'image/jpeg', 0.92);
  };

  // ── Member photo ───────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setCurrentImageUrl(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCapturePhoto = () => {
    const video = videoRef.current; const canvas = canvasRef.current;
    if (!video || !canvas) { setCameraError('No se pudo capturar la imagen.'); return; }
    canvas.width = video.videoWidth || 960; canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { setCameraError('No se pudo generar la foto.'); return; }
      const file = new File([blob], `socio-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setImage(file); setCurrentImageUrl(URL.createObjectURL(blob));
      stopStream(mediaStreamRef); setCameraOpen(false);
    }, 'image/jpeg', 0.92);
  };

  // Un dato, un render. Esta conversión —de la previsualización que se ve en pantalla a
  // un fichero que se pueda subir— vivía SOLO aquí dentro, así que la foto de cara del
  // socio se guardaba y las dos del DNI no: se descargaban, se pintaban al lado, y al
  // dar a guardar se tiraban. El empleado las veía, y la ficha salía sin ellas.
  const urlAFichero = async (url, nombre) => {
    if (!url) return null;
    try {
      const respuesta = await fetch(url);
      const blob = await respuesta.blob();
      if (!blob || !blob.size) return null;
      return new File([blob], nombre, { type: blob.type || 'image/jpeg' });
    } catch {
      return null;
    }
  };

  const resolveCreateImageFile = async () => {
    if (image) return image;
    return urlAFichero(currentImageUrl, `socio-prefill-${Date.now()}.jpg`);
  };

  /** Las dos del DNI. Lo que el empleado elija A MANO manda sobre lo que trajo el QR. */
  const resolveCreateDniFiles = async () => ({
    front: dniFrontFile || await urlAFichero(dniPrefillFrontUrl, `dni-anverso-${Date.now()}.jpg`),
    back:  dniBackFile  || await urlAFichero(dniPrefillBackUrl,  `dni-reverso-${Date.now()}.jpg`),
  });

  // ── Submit ─────────────────────────────────────────────────────────────
  // Delegado en el helper puro (src/lib/dates.js) para que valide de verdad: antes
  // montaba `${y}-${m}-${d}` sin comprobar nada, así que `01/01/199` se enviaba como
  // '199-01-01' y el backend lo rechazaba. Se conserva el centinela '2000-01-01'
  // porque los llamantes de abajo comparan contra él.
  const formatDate = (s) => toIsoDateOrFallback(s);

  // Este modal también edita staff y owners, que entran con su email: para ellos
  // sigue siendo obligatorio. Solo el socio (alta o edición) puede no tener email;
  // el backend guarda un NULL de verdad (nunca un placeholder inventado).
  const privSesionEditada = clientData?.access_privileges;
  const privEditado = typeof privSesionEditada === 'string' ? privSesionEditada : privSesionEditada?.value || privSesionEditada;
  const emailObligatorio = !!id && (privEditado === 'staff' || privEditado === 'owner');

  const submitRegistration = async () => {
    setLocalError(''); setIsSubmitting(true);

    if (!id && sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios) {
      setLocalError('Límite de socios alcanzado. Mejora tu plan para añadir más.');
      setIsSubmitting(false); return;
    }
    // Un socio puede ir sin email; si escribió algo, ese algo sí se valida.
    if ((emailObligatorio || email.trim()) && !isValidEmail(email)) {
      setLocalError('Por favor, ingresa un email válido.'); setIsSubmitting(false); return;
    }
    if (!shopId) {
      setLocalError('Error: No se pudo identificar el venue. Recarga la página.'); setIsSubmitting(false); return;
    }
    if (!id && firmaObligatoria && !signatureDataUrl) {
      setLocalError(TEXTO_FIRMA_OBLIGATORIA); setIsSubmitting(false); return;
    }
    if (isPreRegistrationFlow && !caducidadInput.trim()) {
      setLocalError('Define la fecha de caducidad antes de registrar el socio.'); setIsSubmitting(false); return;
    }

    // Fecha a medio escribir (p. ej. '01/01/199' al tabular antes de acabar el año).
    // Antes se enviaba mutilada y el backend respondía un 500 opaco; ahora se avisa
    // aquí, diciendo qué campo es. Vacío sigue siendo válido: son campos opcionales.
    const fechasAmedias = [
      ['Fecha de nacimiento', fechaNacimientoSocioInput],
      ['Fecha de inscripción', inscripcionInput],
      ['Fecha de caducidad', caducidadInput],
    ].filter(([, value]) => isIncompleteDate(value));
    if (fechasAmedias.length > 0) {
      setLocalError(
        `${fechasAmedias.map(([label]) => label).join(', ')}: revisa el formato (DD/MM/AAAA).`
      );
      setIsSubmitting(false); return;
    }

    // El teléfono es OPCIONAL y de texto libre: no se valida ni se normaliza nada.
    // El gate que había aquí es el que tumbaba el alta con "Teléfono no válido".
    const phoneNormalized = telefonoSocioInput || '';

    const imageToUpload = !id ? await resolveCreateImageFile() : image;
    const { front: dniFrontToUpload, back: dniBackToUpload } = !id
      ? await resolveCreateDniFiles()
      : { front: dniFrontFile, back: dniBackFile };
    if (!id && !imageToUpload) {
      setLocalError('Añade una foto del socio desde tu dispositivo o usando la cámara.'); setIsSubmitting(false); return;
    }

    const nationalId = normalizeNationalIdInput(dniSocioInput);
    const profileData = {};
    let enrollmentIsoForCallback;
    let expirationIsoForCallback;

    if (id) {
      if (nombreSocioInput) profileData.first_name = nombreSocioInput;
      if (apellidoSocioInput) profileData.last_name = apellidoSocioInput;
      if (nationalId) profileData.national_id = nationalId;
      const bd = formatDate(fechaNacimientoSocioInput);
      if (bd !== '2000-01-01') profileData.birth_date = bd;
      // Se mandan SIEMPRE, también vacíos: si solo viajaran con valor, no habría
      // forma de limpiar el «Madrid · 28001» que el alta inventaba (CRUO, 21-09-2026).
      // Vacío = '' / 0, igual que en el alta y que `address`.
      profileData.postal_code = parseInt(cpSocioInput) || 0;
      profileData.city = ciudadSocioInput.trim();
      profileData.address = direccionSocioInput || '';
      profileData.phone = phoneNormalized;
      const enr = formatDate(inscripcionInput);
      if (enr !== '2000-01-01') profileData.enrollment_date = enr;
      const exp = formatDate(caducidadInput);
      if (exp !== '2000-01-01') profileData.expiration_date = exp;
      if (socioAvaladoSel?.id) profileData.guarantor_user_id = socioAvaladoSel.id;
      else if (socioAvaladoInput) profileData.guarantor_member = socioAvaladoInput;
      if (lineaCosteInput) profileData.cost_line = lineaCosteInput;
      if (descuentoInput) profileData.discount = descuentoInput;
      // Siempre, también vacía: si no, un socio guardado con el placeholder «ej:
      // Mensual» como cuota (UI-027) no se podía limpiar desde la ficha.
      profileData.fee = cuotaInput.trim();
      if (consumoMaxInput) profileData.max_monthly_consumption = parseInt(consumoMaxInput);
      profileData.notes = notesInput.trim();
      profileData.consumption_type = { ludenos: consumptionTypeInput === 'ludenos', terap: consumptionTypeInput === 'terap' };
    } else {
      const todayIso = new Date().toISOString().split('T')[0];
      let enrollmentIso = formatDate(inscripcionInput);
      if (!inscripcionInput.trim() || enrollmentIso === '2000-01-01') enrollmentIso = todayIso;
      let expirationIso = formatDate(caducidadInput);
      if (!caducidadInput.trim() || expirationIso === '2000-01-01') {
        expirationIso = defaultExpirationAfterEnrollment(enrollmentIso);
      }
      if (expirationIso <= enrollmentIso) {
        expirationIso = defaultExpirationAfterEnrollment(enrollmentIso);
      }
      enrollmentIsoForCallback = enrollmentIso;
      expirationIsoForCallback = expirationIso;

      profileData.first_name = nombreSocioInput || 'Cliente';
      profileData.last_name  = apellidoSocioInput || 'Nuevo';
      // NUNCA inventar un DNI: metía '12345678' a todo socio sin documento, el
      // primero se quedaba el valor y el resto no podían darse de alta ("el DNI ya
      // existe"). Sin documento se manda vacío y el backend lo acepta.
      profileData.national_id = nationalId || '';
      profileData.birth_date  = formatDate(fechaNacimientoSocioInput) || '2000-01-01';
      // NUNCA inventar ciudad ni CP: sin dato se mandaba «Madrid» y 28001 a todo
      // socio que llegaba sin ellos (la preinscripción no los pide y el anverso del
      // DNI no los trae), y CRUO veía «Madrid» en fichas de gente que no vive allí.
      // Vacío se manda vacío ('' y 0); el backend lo acepta y la ficha dice «Sin indicar».
      profileData.postal_code = parseInt(cpSocioInput) || 0;
      profileData.city        = ciudadSocioInput.trim();
      profileData.address     = direccionSocioInput || '';
      profileData.phone       = phoneNormalized;
      profileData.enrollment_date  = enrollmentIso;
      profileData.expiration_date  = expirationIso;
      // Vacío se guarda vacío: el placeholder «ej: Mensual» acababa en la ficha
      // como si fuera la cuota (auditoría de UI, UI-027); la ficha ya dice «Sin indicar».
      profileData.fee              = cuotaInput.trim();
      profileData.cost_line        = lineaCosteInput || 'Defecto';
      profileData.discount         = descuentoInput  || 'Sin descuento';
      profileData.max_monthly_consumption = parseInt(consumoMaxInput) || 0;
      profileData.notes            = notesInput.trim();
      profileData.consumption_type = { ludenos: consumptionTypeInput === 'ludenos', terap: consumptionTypeInput === 'terap' };
      profileData.credit_balance   = 0.0;
      if (socioAvaladoSel?.id) profileData.guarantor_user_id = socioAvaladoSel.id;
      else if (socioAvaladoInput) profileData.guarantor_member = socioAvaladoInput;
    }

    // Vacío viaja como null, nunca como '': el backend distingue «sin email» de un
    // email vacío que chocaría con el de otro socio sin email.
    const payload = { email: emailObligatorio ? email : (email.trim() || null), profile: profileData };
    // Izadas fuera del bloque del alta: el cobro aparte de la cuota (tras crear
    // al socio) las necesita, y una const dentro del if no llega hasta allí.
    let cuotaAparte = false;
    let feeAmount = NaN;
    let hayDescuento = false;
    let dtoNum = 0;
    if (!id) {
      payload.password = '123456';
      payload.venue_id = shopId;
      // Optional Nº socio override (numeric). If left at the previewed value or
      // blank, the backend gap-fills. Backend returns 409 if the number is taken.
      const mid = parseInt(membershipInput, 10);
      if (!Number.isNaN(mid)) payload.membership_id = mid;
      // Firma del acta (data URL del canvas). El backend genera el acta firmada.
      if (signatureDataUrl) payload.signature = signatureDataUrl;
      // La del avalador solo viaja si sigue habiendo avalador elegido: quitarlo
      // desmonta su recuadro pero no borra el estado, y una firma sin avalador
      // en el acta sería de nadie.
      if (firmaAvaladorDataUrl && socioAvaladoInput) payload.guarantor_signature = firmaAvaladorDataUrl;
      // Cuota de inscripción: el backend la cobra al alta si el checkbox está marcado.
      // El importe editable de esta alta viaja como override; si coincide con el por
      // defecto del local da igual, el backend aplica lo que llegue.
      feeAmount = parseFloat(feeAmountInput);
      const quiereCuota = !!(chargeInscriptionFee && !Number.isNaN(feeAmount) && feeAmount > 0);
      dtoNum = Number(feeDtoValor);
      hayDescuento = feeDtoValor !== '' && Number.isFinite(dtoNum) && dtoNum > 0;
      // A deber sin descuento = el camino de siempre: la deuda la aplica el alta
      // en el mismo request. Con método efectivo/tarjeta o con descuento, la
      // cuota se cobra APARTE tras crear al socio (POST /charge-membership-fee):
      // así «abre la caja primero» le llega al staff a la cara, en vez de
      // tragarse en un try/except del alta. El campo legacy
      // inscription_fee_payment_method siempre dijo 'cash' mientras el backend
      // aplicaba deuda — por eso no se puede honrar retroactivamente y el cobro
      // nuevo va por el endpoint explícito.
      cuotaAparte = quiereCuota && (feePaymentMethod !== 'debt' || hayDescuento);
      payload.charge_inscription_fee = quiereCuota && !cuotaAparte;
      if (payload.charge_inscription_fee) payload.inscription_fee_amount = feeAmount;
      payload.inscription_fee_payment_method = 'cash';
    }

    const userAccess = clientData?.access_privileges;
    const priv = typeof userAccess === 'string' ? userAccess : userAccess?.value || userAccess;
    const isStaff = id && priv === 'staff';
    const isOwner = id && priv === 'owner';
    const formKey = isStaff ? 'staff_data' : isOwner ? 'owner_data' : 'client_data';
    const fileKey = isOwner ? 'image' : 'documents';

    const formData = new FormData();
    formData.append(formKey, JSON.stringify(payload));
    if (imageToUpload) formData.append(fileKey, imageToUpload);
    if (dniFrontToUpload) formData.append('dni_front_document', dniFrontToUpload);
    if (dniBackToUpload) formData.append('dni_back_document', dniBackToUpload);

    try {
      let url; const method = id ? 'put' : 'post';
      if (id) {
        url = isStaff ? `/v1/staff/${id}` : isOwner ? `/v1/owners/${id}` : `/v1/clients/${id}`;
      } else url = '/v1/clients';

      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      setLocalError(''); setIsSubmitting(false);

      if (!id && response.data && onClientCreated) {
        try {
          await onClientCreated(response.data, {
            enrollment_date: enrollmentIsoForCallback,
            expiration_date: expirationIsoForCallback,
          });
        } catch { /* non-blocking */ }
      }

      // Cuota con método/descuento: cobro aparte, ya con el socio creado. Si
      // falla, el socio EXISTE — se dice qué quedó sin hacer y cómo arreglarlo
      // (desde su ficha), y el modal se queda abierto para que se lea.
      if (!id && response.data?.id && cuotaAparte) {
        try {
          const params = new URLSearchParams({ venue_id: shopId });
          params.set('amount', String(feeAmount));
          params.set('payment_method', feePaymentMethod);
          if (hayDescuento) {
            params.set('discount_type', feeDtoTipo);
            params.set('discount_value', String(dtoNum));
          }
          const resCuota = await fetch(`/v1/charge-membership-fee/${response.data.id}?${params.toString()}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          const datos = await resCuota.json().catch(() => ({}));
          if (!resCuota.ok) {
            const detalle = typeof datos?.detail === 'string' ? datos.detail : 'no se pudo cobrar';
            throw new Error(detalle);
          }
        } catch (feeErr) {
          setLocalError(
            `Socio creado, pero la cuota NO se pudo cobrar: ${feeErr.message}. ` +
            'Cóbrala desde su ficha (Renovar → Cobrar la cuota).'
          );
          setIsSubmitting(false);
          return; // el modal se queda abierto: el aviso tiene que leerse
        }
      }
      if (id && response.data && onClientUpdated) {
        try {
          invalidateImageCache(getImageCacheKey('client', response.data.id || id));
          await onClientUpdated(response.data);
        } catch { /* non-blocking */ }
      }

      // Reset
      setEmail(''); setImage(null);
      setNombreSocioInput(''); setApellidoSocioInput(''); setDniSocioInput('');
      // Ficha nueva: el escaneo del socio anterior no cuenta como «lo escribió un escaneo».
      dniScanWrittenRef.current = {}; dniScanDataRef.current = null;
      setDniScanFilled([]); setDniScanConflicts([]);
      setFechaNacimientoSocioInput(''); setDatePickerValue('');
      setInscripcionInput(''); setDatePickerInscripcion('');
      setCaducidadInput(''); setDatePickerCaducidad('');
      setTelefonoSocioInput(''); setDireccionSocioInput('');
      setSocioAvaladoInput(''); setSocioAvaladoSel(null); setLineaCosteInput('Defecto');
      setDescuentoInput('Sin descuento'); setCuotaInput('');
      setConsumoMaxInput(''); setConsumptionTypeInput(null);
      setNotesInput(''); setDniFrontFile(null); setDniBackFile(null); setDniPartial(null);
      setSignatureDataUrl(null);
      setIsDirty(false);
      handleModal();
    } catch (error) {
      setIsSubmitting(false);
      let msg = id ? 'Error al actualizar el socio' : 'Error al crear el socio';
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        msg = detail.map((e) => {
          let m = e.msg;
          if (m.includes('national_id') || m.toLowerCase().includes('dni')) m = 'El DNI ya existe.';
          else if (esEmailDuplicado(m)) m = 'El email ya existe.';
          return m;
        }).join('; ');
      } else if (detail === 'signature_required') {
        // La red del servidor (por si el flag no cargó): el mismo texto que la guarda local.
        msg = TEXTO_FIRMA_OBLIGATORIA;
      } else if (typeof detail === 'string') {
        msg = detail.toLowerCase().includes('national_id') || detail.toLowerCase().includes('dni')
          ? 'El DNI ya existe. Verifica que no esté duplicado.'
          : esEmailDuplicado(detail) ? 'El email ya existe.'
          // El teléfono ya no valida nada (05-09-2026), así que no se reescribe su
          // error: si el backend llegara a quejarse de este campo, que se lea lo que
          // dice de verdad y no el «usa formato internacional» que veníamos a matar.
          : detail;
      } else if (error.message) msg = error.message;
      setLocalError(msg);
      if (setErrorMessage) setErrorMessage(msg);
    }
  };

  const handleConsumptionTypeChange = (value) => {
    setConsumptionTypeInput(value);
  };

  // El formulario arranca limpio cada vez que se abre.
  useEffect(() => { if (active) { setIsDirty(false); setAskDiscard(false); } }, [active]);

  // Cerrar: si hay cambios, se pregunta. Cubre el botón Cancelar, la X y el Esc,
  // porque los tres pasan por aquí.
  const requestClose = () => {
    if (isDirty && !isSubmitting) { setAskDiscard(true); return; }
    handleModal();
  };
  const discardAndClose = () => { setAskDiscard(false); setIsDirty(false); handleModal(); };

  const handleSubmit = (e) => { e.preventDefault(); submitRegistration(); };

  // Lo que se firma, en UN solo sitio: lo lee la vista previa y también es la
  // huella que decide si la firma caduca. Dos copias de esto = una previa que no
  // coincide con lo que caduca.
  const actaDatos = {
    first_name: nombreSocioInput || '',
    last_name: apellidoSocioInput || '',
    national_id: normalizeNationalIdInput(dniSocioInput) || '',
    email: email || '',
    address: direccionSocioInput || '',
    phone: telefonoSocioInput || '',
    city: ciudadSocioInput || '',
    guarantor_member: socioAvaladoInput || '',
    guarantor_number: socioAvaladoSel?.numero ? String(socioAvaladoSel.numero) : '',
    membership_id: membershipInput || (nextMembershipId != null ? String(nextMembershipId) : ''),
    // Igual que al guardar: sin fecha escrita se inscribe hoy. Si aquí se manda
    // vacío, el socio lee un acta sin fecha y se archiva otra con la de hoy.
    enrollment_date: inscripcionInput || new Date().toISOString().split('T')[0],
    max_monthly_consumption: consumoMaxInput || '',
  };

  if (!active) return null;

  const atCap = !id && sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios;
  const modalTitle = id ? 'Editar socio' : (isPreRegistrationFlow ? 'Revisar preinscripción y crear socio' : 'Crear socio');

  // El botón de Registrar en verde en cuanto hay firma: el staff ve de un vistazo
  // que puede seguir, sin acordarse de si el socio llegó a firmar (petición de
  // Antonio). Firmar sigue sin ser obligatorio… salvo que ESTE club lo exija
  // (`prereg_signature_required`, CRUO 11-09-2026): entonces sin firma no hay
  // Registrar, y el motivo se lee al lado — no un botón muerto sin explicación.
  const faltaFirmaExigida = !id && Boolean(firmaObligatoria) && !signatureDataUrl;
  const modalFooter = (
    <>
      <button type="button" className="wa-btn-secondary" onClick={requestClose}>Cancelar</button>
      {faltaFirmaExigida && (
        <span className="self-center text-xs text-amber-700" data-testid="falta-firma-exigida">
          Falta la firma del socio
        </span>
      )}
      <button
        type="submit" form="customer-modal-form"
        disabled={isSubmitting || atCap || faltaFirmaExigida}
        className="wa-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        // En línea a propósito: `figma-forms.css` se carga DESPUÉS de Tailwind y
        // `.wa-btn-primary` gana a `bg-emerald-600` con la misma especificidad,
        // así que por clase el verde no se veía nunca.
        style={!id && signatureDataUrl ? { backgroundColor: '#059669', borderColor: '#059669' } : undefined}
      >
        {isSubmitting ? (id ? 'Actualizando…' : 'Creando…') : (id ? 'Actualizar' : 'Registrar')}
      </button>
    </>
  );

  return (
    <>
      <CustomModal active={active} handleModal={requestClose} title={modalTitle} size="wide" titleId="customer-modal-title" footer={modalFooter}>
        <form id="customer-modal-form" onSubmit={handleSubmit} onChange={markDirty} className="wa-form">

          {localError && (
            <div className="wa-form-error mb-3" role="alert">
              <strong>Error: </strong><span>{localError}</span>
            </div>
          )}

          {!id && sociosCap && (
            <div className={`rounded-lg px-4 py-2 text-sm mb-3 ${
              atCap ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-[var(--wa-muted)] text-[var(--wa-foreground)] border border-[var(--wa-border)]'
            }`}>
              Socios: {sociosCap.socios_count} / {sociosCap.max_socios ?? '∞'}
              {atCap && (
                <button
                  type="button"
                  onClick={() => {
                    handleModal?.();
                    navigate('/settings');
                  }}
                  className="block mt-1 font-medium underline underline-offset-2 hover:no-underline text-left"
                >
                  Límite de socios alcanzado. Ir a Configuración para mejorar el plan.
                </button>
              )}
            </div>
          )}

          {!id && (
            <div className="mb-3 max-w-[220px]">
              <label className="wa-label">Nº socio</label>
              <input
                type="number"
                value={membershipInput}
                onChange={(e) => setMembershipInput(e.target.value)}
                placeholder={nextMembershipId != null ? String(nextMembershipId) : 'auto'}
                className="wa-input"
              />
              <p className="mt-1 text-[11px] text-[var(--wa-muted-foreground)]">
                {nextMembershipId != null
                  ? `Siguiente disponible: ${nextMembershipId}. Puedes cambiarlo.`
                  : 'Se asignará automáticamente.'}
              </p>
            </div>
          )}

          {id && clientData && (
            <div className="rounded-lg border border-[var(--wa-border)] bg-[var(--wa-muted)] px-4 py-2 text-sm mb-3">
              <span className="text-[var(--wa-muted-foreground)]">Saldo actual: </span>
              <span className={`font-semibold ${balanceToneClass(clientData.profile?.credit_balance, 'text-[var(--foreground)]')}`}>{formatBalance(clientData.profile?.credit_balance)}</span>
              <span className="block text-xs text-[var(--wa-muted-foreground)] mt-0.5">Usa «Añadir crédito» para modificar el saldo</span>
            </div>
          )}

          {/* ── DNI scanner status ── */}
          {dniScanning && (
            <div className="flex items-center gap-2 rounded-md bg-[var(--muted)] border border-[var(--border)] px-4 py-2 text-sm text-[var(--foreground)] mb-3">
              <svg className="animate-spin w-4 h-4 text-[var(--primary)] shrink-0" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Leyendo DNI… esto puede tardar unos segundos.
            </div>
          )}
          {dniScanError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700 mb-3 flex items-start justify-between gap-2">
              <span>{dniScanError}</span>
              <button type="button" onClick={() => setDniScanError('')} className="text-red-400 hover:text-red-600 shrink-0">✕</button>
            </div>
          )}
          {dniScanFilled.length > 0 && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-2 text-sm text-blue-800 mb-3 flex items-start justify-between gap-2">
              <span>Rellenado con el documento: <strong>{dniScanFilled.join(', ')}</strong>. Revísalo antes de guardar.</span>
              <button type="button" onClick={() => setDniScanFilled([])} className="text-blue-400 hover:text-blue-600 shrink-0">✕</button>
            </div>
          )}
          {dniScanConflicts.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-900 mb-3">
              <p className="mb-1">No se ha tocado lo que ya estaba escrito. El documento dice:</p>
              {dniScanConflicts.map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-2 py-0.5">
                  <span>{c.label}: <strong>{c.text}</strong></span>
                  <button type="button" onClick={() => applyDniSuggestion(c.key)}
                    className="rounded-lg border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium hover:bg-amber-100 shrink-0">
                    Usar
                  </button>
                </div>
              ))}
            </div>
          )}

          {id ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">

            {/* ── LEFT: Datos principales ─────────────────────────────── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-xs font-medium text-[var(--muted-foreground)]">Datos principales</h3>

                {/* Scan / Upload DNI (create mode only) */}
                {!id && (
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => openDniScanner('front')}
                      className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                      title="Cámara — anverso">
                      📷 Anverso
                    </button>
                    <button type="button" onClick={() => openDniScanner('back')}
                      className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                      title="Cámara — reverso (MRZ)">
                      📷 Reverso
                    </button>
                  </div>
                )}
              </div>

              {/* Nombre + Apellidos */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Nombre</label>
                  <input value={nombreSocioInput} onChange={(e) => setNombreSocioInput(e.target.value)} placeholder="nombre" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Apellidos</label>
                  <input value={apellidoSocioInput} onChange={(e) => setApellidoSocioInput(e.target.value)} placeholder="apellidos" className="wa-input" />
                </div>
              </div>

              {/* DNI + Fecha nacimiento */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">DNI / NIE / Pasaporte</label>
                  <input
                    value={dniSocioInput}
                    onChange={(e) => setDniSocioInput(e.target.value)}
                    placeholder="12345678A, X1234567A, pasaporte…"
                    maxLength={20}
                    className="wa-input"
                  />
                </div>
                <DateField
                  label="Fecha nacimiento"
                  value={fechaNacimientoSocioInput}
                  onChange={setFechaNacimientoSocioInput}
                  pickerValue={datePickerValue}
                  onPickerChange={setDatePickerValue}
                />
              </div>

              {/* Teléfono + Email */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Teléfono</label>
                  {/* Casilla de TEXTO LIBRE (decidido 05-09-2026). Antes había aquí un
                      `PhoneInput` con selector de país que validaba E.164: el club escribía
                      un fijo, dos números o «el de su hermana» y el alta se le caía con
                      "Teléfono no válido". Nadie usa este dato como teléfono —no hay SMS,
                      WhatsApp, llamada ni búsqueda por número—, así que estorbaba y ya está.
                      20 caracteres: lo que aguanta `profiles.phone`. */}
                  {/* `type="tel"` NO valida nada —sigue aceptando letras—, pero en la
                      tablet del club abre el teclado numérico en vez del QWERTY, que
                      es lo que se teclea el 99% de las veces. */}
                  <input
                    type="tel"
                    value={telefonoSocioInput}
                    onChange={(e) => setTelefonoSocioInput(e.target.value)}
                    maxLength={20}
                    placeholder="teléfono (opcional)"
                    className="wa-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label" htmlFor="socio-email">{emailObligatorio ? 'Email' : 'Email (opcional)'}</label>
                  <input id="socio-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="wa-input" required={emailObligatorio} />
                </div>
              </div>

              {/* Dirección */}
              <div className="wa-form-field">
                <label className="wa-label">Dirección</label>
                <input value={direccionSocioInput} onChange={(e) => setDireccionSocioInput(e.target.value)} placeholder="dirección" className="wa-input" />
              </div>

              {/* Duración / Fechas inscripción (edición: mismos atajos que en el alta completa) */}
              <div className="space-y-3">
                <div className="wa-form-field">
                  <label className="wa-label">Duración de inscripción</label>
                  <div className="flex flex-wrap gap-2">
                    {durations.map((d) => {
                      const isActive = selectedDurationId === d.id;
                      return (
                        <button
                          key={d.id} type="button"
                          onClick={() => {
                            const today = new Date();
                            const expiry = sumarDuracion(today, d);
                            setSelectedDurationId(d.id);
                            setInscripcionInput(toDisplay(today));   setDatePickerInscripcion(toISO(today));
                            setCaducidadInput(toDisplay(expiry));    setDatePickerCaducidad(toISO(expiry));
                          }}
                          className={`px-4 py-2 rounded-sm text-sm font-medium border border-solid bg-[var(--muted)] transition-colors ${
                            isActive ? 'border-[var(--primary)] text-[var(--foreground)]'
                                     : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--muted-foreground)]'
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedDurationId && inscripcionInput && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Inscripción: <span className="font-medium text-gray-700">{inscripcionInput}</span>
                      {' · '}Caducidad: <span className="font-medium text-gray-700">{caducidadInput}</span>
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <DateField label="Inscripción" value={inscripcionInput} onChange={setInscripcionInput} pickerValue={datePickerInscripcion} onPickerChange={setDatePickerInscripcion} />
                  <DateField label="Caducidad"   value={caducidadInput}   onChange={setCaducidadInput}   pickerValue={datePickerCaducidad}   onPickerChange={setDatePickerCaducidad} />
                </div>
              </div>

              {/* Documentos DNI */}
              <div className="wa-form-field">
                <span className="wa-label">Documentos DNI</span>
                <div className="flex flex-wrap gap-3 mb-2">
                  {(dniNewFrontPreviewUrl || dniExistingFrontUrl) ? (
                    <div className="flex flex-col items-center gap-1">
                      <img
                        src={dniNewFrontPreviewUrl || dniExistingFrontUrl}
                        alt="DNI anverso"
                        className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                        onClick={() => window.open(dniNewFrontPreviewUrl || dniExistingFrontUrl, '_blank')}
                        title="Ver anverso completo"
                      />
                      <span className="text-xs text-[var(--wa-muted-foreground)]">
                        Anverso{dniNewFrontPreviewUrl ? ' (nuevo)' : ''}
                      </span>
                    </div>
                  ) : null}
                  {(dniNewBackPreviewUrl || dniExistingBackUrl) ? (
                    <div className="flex flex-col items-center gap-1">
                      <img
                        src={dniNewBackPreviewUrl || dniExistingBackUrl}
                        alt="DNI reverso"
                        className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                        onClick={() => window.open(dniNewBackPreviewUrl || dniExistingBackUrl, '_blank')}
                        title="Ver reverso completo"
                      />
                      <span className="text-xs text-[var(--wa-muted-foreground)]">
                        Reverso{dniNewBackPreviewUrl ? ' (nuevo)' : ''}
                      </span>
                    </div>
                  ) : null}
                  {!dniNewFrontPreviewUrl && !dniExistingFrontUrl && !dniNewBackPreviewUrl && !dniExistingBackUrl && (
                    <span className="text-xs text-[var(--wa-muted-foreground)]">Sin escaneos de DNI</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openDniScanner('front')}
                    className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                    title="Cámara — anverso">
                    Cámara anverso
                  </button>
                  <button type="button" onClick={() => openDniScanner('back')}
                    className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                    title="Cámara — reverso">
                    Cámara reverso
                  </button>
                  <button type="button" onClick={() => dniGalleryFrontRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Subir anverso
                  </button>
                  <button type="button" onClick={() => dniGalleryBackRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Subir reverso
                  </button>
                </div>
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">
                  Sube nuevas imágenes solo si quieres reemplazar los escaneos actuales.
                </p>
              </div>

              {/* Foto */}
              <div className="wa-form-field">
                <label className="wa-label">{id ? 'Cambiar foto (opcional)' : 'Foto del socio'}</label>
                {currentImageUrl && (
                  <div className="mb-2">
                    <img src={currentImageUrl} alt="Foto socio" className="w-16 h-16 rounded-full object-cover border border-[var(--wa-border)]" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => galleryInputRef.current?.click()} className="wa-btn-secondary h-9 text-sm px-3">
                    Desde dispositivo
                  </button>
                  <button type="button" onClick={() => setCameraOpen(true)} className="wa-btn-primary h-9 text-sm px-3">
                    Hacer foto
                  </button>
                </div>
                <input ref={galleryInputRef}  type="file" accept="image/*"          onChange={handleImageChange} className="hidden" />
                <input ref={cameraInputRef}   type="file" accept="image/*" capture="user" onChange={handleImageChange} className="hidden" />
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">
                  {id ? 'Deja vacío para mantener la foto actual.' : 'Requerida para crear el socio.'}
                </p>
              </div>
            </div>

            {/* ── RIGHT: Datos adicionales ─────────────────────────────── */}
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)] mb-1">Datos adicionales</h3>

              {/* Socio avalado + Línea de coste */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Socio avalado</label>
                  <AvalistaField token={token} shopId={shopId} value={socioAvaladoInput} onChange={setSocioAvaladoInput} selected={socioAvaladoSel} onSelect={setSocioAvaladoSel} />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Línea de coste</label>
                  <select value={lineaCosteInput} onChange={(e) => setLineaCosteInput(e.target.value)} className="wa-select">
                    <option>Defecto</option><option>Segunda</option><option>Tercera</option>
                  </select>
                </div>
              </div>

              {/* CP + Ciudad */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">CP</label>
                  <input value={cpSocioInput} onChange={(e) => setCpSocioInput(e.target.value)} placeholder="cp" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Ciudad</label>
                  <input value={ciudadSocioInput} onChange={(e) => setCiudadSocioInput(e.target.value)} placeholder="ciudad" className="wa-input" />
                </div>
              </div>

              {/* Descuento + Cuota */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Descuento</label>
                  <select value={descuentoInput} onChange={(e) => setDescuentoInput(e.target.value)} className="wa-select">
                    <option>Sin descuento</option>
                    <option>5%</option><option>10%</option><option>15%</option>
                    <option>20%</option><option>50%</option><option>100%</option>
                  </select>
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Cuota</label>
                  <input type="text" value={cuotaInput} onChange={(e) => setCuotaInput(e.target.value)} placeholder="ej: Mensual" className="wa-input" />
                </div>
              </div>

              {/* Consumo + Tipo */}
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Consumo máx/mes</label>
                  <input type="number" value={consumoMaxInput} onChange={(e) => setConsumoMaxInput(e.target.value)} placeholder="0" min="0" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <span className="wa-label">Tipo de consumo</span>
                  <div className="flex flex-wrap gap-4 items-center min-h-[40px]">
                    <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                      <input type="radio" name="consumption_type" value="ludenos" checked={consumptionTypeInput === 'ludenos'} onChange={(e) => handleConsumptionTypeChange(e.target.value)} />
                      Lúd.
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[var(--wa-foreground)] cursor-pointer">
                      <input type="radio" name="consumption_type" value="terap" checked={consumptionTypeInput === 'terap'} onChange={(e) => handleConsumptionTypeChange(e.target.value)} />
                      Terap.
                    </label>
                  </div>
                </div>
              </div>

              {/* Documentos */}
              <div className="wa-form-field">
                <label className="wa-label">Notas</label>
                <textarea
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  className="wa-textarea wa-textarea-compact"
                  rows={5}
                  placeholder="Observaciones internas sobre este socio"
                />
              </div>
            </div>
          </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

            {/* DATOS PERSONALES */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)]">Datos personales</h3>

              {/* Foto del socio (arriba, como Figma) */}
              <div className="wa-form-field">
                <label className="wa-label">Foto del socio</label>
                {currentImageUrl && (
                  <div className="mb-2">
                    <img src={currentImageUrl} alt="Foto socio" className="w-16 h-16 rounded-full object-cover border border-[var(--wa-border)]" />
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setCameraOpen(true)} className="wa-btn-primary h-9 text-sm px-3">
                    Hacer foto
                  </button>
                  <button type="button" onClick={() => galleryInputRef.current?.click()} className="wa-btn-secondary h-9 text-sm px-3">
                    Desde dispositivo
                  </button>
                </div>
                <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="user" onChange={handleImageChange} className="hidden" />
                <p className="text-xs text-[var(--wa-muted-foreground)] mt-1">Requerida para crear el socio.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Nombre</label>
                  <input value={nombreSocioInput} onChange={(e) => setNombreSocioInput(e.target.value)} placeholder="nombre" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Apellidos</label>
                  <input value={apellidoSocioInput} onChange={(e) => setApellidoSocioInput(e.target.value)} placeholder="apellidos" className="wa-input" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">DNI / NIE / Pasaporte</label>
                  <input
                    value={dniSocioInput}
                    onChange={(e) => setDniSocioInput(e.target.value)}
                    placeholder="12345678A, X1234567A, pasaporte…"
                    maxLength={20}
                    className="wa-input"
                  />
                </div>
                <DateField
                  label="Fecha nacimiento"
                  value={fechaNacimientoSocioInput}
                  onChange={setFechaNacimientoSocioInput}
                  pickerValue={datePickerValue}
                  onPickerChange={setDatePickerValue}
                />
              </div>
            </section>

            {/* MEMBRESÍA */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)]">Membresía</h3>

              <div className="wa-form-field">
                <label className="wa-label">Socio avalado</label>
                <AvalistaField token={token} shopId={shopId} value={socioAvaladoInput} onChange={setSocioAvaladoInput} selected={socioAvaladoSel} onSelect={setSocioAvaladoSel} />
              </div>

              <div className="wa-form-field">
                <label className="wa-label">Duración de inscripción</label>
                <div className="flex flex-wrap gap-2">
                  {durations.map((d) => {
                    const isActive = selectedDurationId === d.id;
                    return (
                      <button
                        key={d.id} type="button"
                        onClick={() => {
                          const today = new Date();
                          const expiry = sumarDuracion(today, d);
                          setSelectedDurationId(d.id);
                          setInscripcionInput(toDisplay(today));   setDatePickerInscripcion(toISO(today));
                          setCaducidadInput(toDisplay(expiry));    setDatePickerCaducidad(toISO(expiry));
                        }}
                        className={`px-4 py-2 rounded-sm text-sm font-medium border border-solid bg-[var(--muted)] transition-colors ${
                          isActive ? 'border-[var(--primary)] text-[var(--foreground)]'
                                   : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--muted-foreground)]'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                {selectedDurationId && inscripcionInput && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Inscripción: <span className="font-medium text-gray-700">{inscripcionInput}</span>
                    {' · '}Caducidad: <span className="font-medium text-gray-700">{caducidadInput}</span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Inscripción" value={inscripcionInput} onChange={setInscripcionInput} pickerValue={datePickerInscripcion} onPickerChange={setDatePickerInscripcion} />
                <DateField label="Caducidad"   value={caducidadInput}   onChange={setCaducidadInput}   pickerValue={datePickerCaducidad}   onPickerChange={setDatePickerCaducidad} />
              </div>

              {inscriptionFee != null && (
                <div className="wa-form-field pt-1">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={chargeInscriptionFee}
                      onChange={(e) => setChargeInscriptionFee(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-[var(--border)] accent-[var(--primary)] focus:ring-[var(--ring-selected)]"
                    />
                    <span className="text-sm text-gray-700">
                      Cobrar cuota de inscripción
                      <span className="block text-xs text-gray-400">Desmárcalo para socios que vienen del Kong Club (inscripción gratuita).</span>
                    </span>
                  </label>
                  {chargeInscriptionFee && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={feeAmountInput}
                          onChange={(e) => setFeeAmountInput(e.target.value)}
                          className="wa-input w-28 pr-7"
                          aria-label="Importe de la cuota de inscripción"
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
                      </div>
                      <select
                        value={feePaymentMethod}
                        onChange={(e) => setFeePaymentMethod(e.target.value)}
                        aria-label="Método de cobro de la cuota"
                        className="wa-input h-9 w-auto text-xs"
                      >
                        <option value="debt">A deber (baja el saldo)</option>
                        <option value="cash">Efectivo (entra en caja)</option>
                        <option value="card">Tarjeta (datáfono)</option>
                      </select>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={feeDtoValor}
                          onChange={(e) => setFeeDtoValor(e.target.value)}
                          placeholder="Dto."
                          aria-label="Descuento de la cuota"
                          className="wa-input h-9 w-16 text-xs tabular-nums"
                        />
                        <select
                          value={feeDtoTipo}
                          onChange={(e) => setFeeDtoTipo(e.target.value)}
                          aria-label="Unidad del descuento"
                          className="wa-input h-9 w-auto px-1 text-xs"
                        >
                          <option value="percent">%</option>
                          <option value="amount">€</option>
                        </select>
                      </div>
                      {Number(feeAmountInput) !== inscriptionFee && (
                        <span className="text-xs text-gray-400">
                          Por defecto del local: {inscriptionFee.toFixed(2)} €
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* CONTACTO */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)]">Contacto</h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Teléfono</label>
                  {/* Casilla de TEXTO LIBRE (decidido 05-09-2026). Antes había aquí un
                      `PhoneInput` con selector de país que validaba E.164: el club escribía
                      un fijo, dos números o «el de su hermana» y el alta se le caía con
                      "Teléfono no válido". Nadie usa este dato como teléfono —no hay SMS,
                      WhatsApp, llamada ni búsqueda por número—, así que estorbaba y ya está.
                      20 caracteres: lo que aguanta `profiles.phone`. */}
                  {/* `type="tel"` NO valida nada —sigue aceptando letras—, pero en la
                      tablet del club abre el teclado numérico en vez del QWERTY, que
                      es lo que se teclea el 99% de las veces. */}
                  <input
                    type="tel"
                    value={telefonoSocioInput}
                    onChange={(e) => setTelefonoSocioInput(e.target.value)}
                    maxLength={20}
                    placeholder="teléfono (opcional)"
                    className="wa-input"
                  />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label" htmlFor="socio-email">{emailObligatorio ? 'Email' : 'Email (opcional)'}</label>
                  <input id="socio-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="wa-input" required={emailObligatorio} />
                </div>
              </div>

              <div className="wa-form-field">
                <label className="wa-label">Dirección</label>
                <input value={direccionSocioInput} onChange={(e) => setDireccionSocioInput(e.target.value)} placeholder="dirección" className="wa-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">CP</label>
                  <input value={cpSocioInput} onChange={(e) => setCpSocioInput(e.target.value)} placeholder="cp" className="wa-input" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Ciudad</label>
                  <input value={ciudadSocioInput} onChange={(e) => setCiudadSocioInput(e.target.value)} placeholder="ciudad" className="wa-input" />
                </div>
              </div>
            </section>

            {/* DOCUMENTACIÓN (DNI) */}
            <section className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)]">Documentación (DNI)</h3>

              {/* Miniaturas de lo que hay: lo capturado/subido en este alta pisa lo que
                  trajo la preinscripción. Hasta el 22-09-2026 el alta solo enseñaba las de
                  la preinscripción: se capturaba el DNI con la cámara y no se veía nada,
                  aunque se guardara bien (en Editar sí salían). */}
              {(() => {
                const anverso = dniNewFrontPreviewUrl || dniPrefillFrontUrl;
                const reverso = dniNewBackPreviewUrl || dniPrefillBackUrl;
                if (!anverso && !reverso) return null;
                const hayPrefill = dniPrefillFrontUrl || dniPrefillBackUrl;
                return (
                  <div className="wa-form-field">
                    <span className="wa-label">{hayPrefill ? 'DNI preinscripción' : 'DNI capturado'}</span>
                    <div className="flex flex-wrap gap-3">
                      {anverso && (
                        <div className="flex flex-col items-center gap-1">
                          <img src={anverso} alt="DNI anverso" className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                            onClick={() => window.open(anverso, '_blank')} title="Ver anverso completo" />
                          <span className="text-xs text-[var(--wa-muted-foreground)]">Anverso{dniNewFrontPreviewUrl && hayPrefill ? ' (nuevo)' : ''}</span>
                        </div>
                      )}
                      {reverso && (
                        <div className="flex flex-col items-center gap-1">
                          <img src={reverso} alt="DNI reverso" className="h-20 rounded-lg border border-[var(--wa-border)] object-cover cursor-pointer"
                            onClick={() => window.open(reverso, '_blank')} title="Ver reverso completo" />
                          <span className="text-xs text-[var(--wa-muted-foreground)]">Reverso{dniNewBackPreviewUrl && hayPrefill ? ' (nuevo)' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="wa-form-field">
                <span className="wa-label">DNI — cámara</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => openDniScanner('front')}
                    className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                    title="Cámara — anverso">
                    Anverso
                  </button>
                  <button type="button" onClick={() => openDniScanner('back')}
                    className="inline-flex h-8 items-center gap-1 rounded-sm border border-[var(--border)] px-3 text-xs font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                    title="Cámara — reverso (MRZ)">
                    Reverso
                  </button>
                </div>
              </div>

              <div className="wa-form-field">
                <span className="wa-label">DNI — desde dispositivo (imagen)</span>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => dniGalleryFrontRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Anverso
                  </button>
                  <button type="button" onClick={() => dniGalleryBackRef.current?.click()}
                    className="wa-btn-secondary h-9 text-sm px-3">
                    Reverso
                  </button>
                </div>
              </div>
            </section>
          </div>
          )}

          {/* ── Acta de inscripción (firma al crear · ver al editar) ── */}
          {!id ? (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)] mb-1">
                Acta de inscripción{firmaObligatoria ? ' · firma obligatoria' : ''}
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                El socio lee el acta y firma al final. Antes había aquí un recuadro de
                firma suelto: se firmaba un documento que no se había visto.
                {firmaObligatoria ? ' Este club no da de alta sin la firma.' : ''}
              </p>
              <FirmarDocumento
                value={signatureDataUrl}
                onChange={(v) => {
                  setSignatureDataUrl(v);
                  // Solo deja de ser "del móvil" si el trazo CAMBIA. Abrir el
                  // visor para leerlo y darle a «Firmar y cerrar» sin dibujar
                  // nada devuelve la misma firma: si eso la marcara como de
                  // tablet, recuperaría la caducidad y el primer cambio de dato
                  // —incluso el nº de socio llegando solo— borraría la firma del
                  // socio, que ya no está delante para repetirla.
                  if (v !== firmaMovilRef.current) setFirmaDelMovil(false);
                }}
                // La firma que el socio dio en SU móvil no caduca aunque el club
                // complete datos al aprobar: no está delante para volver a
                // firmar (decidido 02-09). La que se hace aquí, en la tablet, sí.
                caducaAlCambiar={!firmaDelMovil}
                pedirActa={async () => {
                  const res = await axios.post(
                    `/v1/venues/${shopId}/acta/preview`,
                    actaDatos,
                    { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
                  );
                  return res.data;
                }}
                datos={actaDatos}
              />
              {firmaDelMovil && (
                <p className="mt-2 text-xs text-gray-500">
                  Firmada por el socio desde su móvil al enviar la preinscripción.
                </p>
              )}
              {socioAvaladoInput && (
                <div className="mt-3">
                  <FirmarDocumento
                    value={firmaAvaladorDataUrl}
                    onChange={setFirmaAvaladorDataUrl}
                    pedirActa={async () => {
                      const res = await axios.post(
                        `/v1/venues/${shopId}/acta/preview`,
                        actaDatos,
                        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
                      );
                      return res.data;
                    }}
                    datos={actaDatos}
                    textos={{
                      firmar: 'Firma del avalador',
                      firmado: '✓ Avalador ha firmado',
                      verOFirmar: 'Ver o volver a firmar (avalador)',
                      tuFirma: 'Firma del socio avalador',
                      firmaAyuda: 'El avalador firma con el dedo dentro del recuadro.',
                    }}
                  />
                </div>
              )}
              {firmaMovilError && (
                <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  El socio firmó al preinscribirse, pero <strong>no se ha podido recuperar
                  esa firma</strong>. {firmaObligatoria
                    ? 'Este club exige el acta firmada: pide al socio que firme aquí.'
                    : 'Si das el alta ahora, el acta se guardará sin firmar: pide al socio que firme aquí.'}
                </p>
              )}
            </div>
          ) : (clientData?.profile?.inscription_document_path ? (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h3 className="text-xs font-medium text-[var(--muted-foreground)] mb-2">Acta de inscripción</h3>
              <button
                type="button"
                className="wa-btn-secondary h-9 text-sm px-3"
                onClick={async () => {
                  try {
                    const res = await fetch(`/v1/clients/${id}/acta`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) { alert('No se pudo cargar el acta'); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } catch { alert('Error al abrir el acta'); }
                }}
              >
                📄 Ver acta firmada
              </button>
            </div>
          ) : null)}
        </form>
        {/* Las dos cámaras van DENTRO del modal, no como hermanas: `CustomModal` es un
           portal a body con z máximo (#741), y un overlay hermano dentro de #root no
           puede quedar encima por mucho z que lleve — se veía el fondo oscurecerse y
           nada más (staging, 22-09-2026; en prod desde el 12-09). Dentro heredan la
           capa del modal, igual que el visor del acta (FirmarDocumento). */}
      {/* ── Member photo camera overlay ────────────────────────────────── */}
      {cameraOpen && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Foto del socio</h3>
                <p className="text-sm text-gray-500">Coloca al socio frente a la cámara.</p>
              </div>
              <button type="button" onClick={() => { stopStream(mediaStreamRef); setCameraOpen(false); }} className="rounded-full px-3 py-1 text-2xl text-gray-500 hover:bg-gray-100">×</button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-950">
              <video ref={videoRef} autoPlay playsInline muted className="max-h-[55vh] w-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
            </div>
            {cameraError && <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">{cameraError}</div>}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => cameraInputRef.current?.click()} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Selector dispositivo</button>
              <button type="button" onClick={() => { stopStream(mediaStreamRef); setCameraOpen(false); }} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={handleCapturePhoto} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Capturar foto</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DNI camera overlay ────────────────────────────────────────── */}
      {dniCameraOpen && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {dniCameraSide === 'back' ? 'Escanear reverso del DNI' : 'Escanear anverso del DNI'}
                </h3>
                <p className="text-sm text-gray-500">
                  {dniCameraSide === 'back'
                    ? 'Coloca la parte trasera del DNI dentro del encuadre.'
                    : 'Coloca la parte delantera del DNI dentro del encuadre.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { stopStream(dniMediaStreamRef); setDniCameraOpen(false); }}
                className="rounded-full px-3 py-1 text-2xl text-gray-500 hover:bg-gray-100"
              >
                ×
              </button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-950">
              <video ref={dniVideoRef} autoPlay playsInline muted className="max-h-[55vh] w-full object-cover" />
              <canvas ref={dniCanvasRef} className="hidden" />
            </div>
            {dniCameraError && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {dniCameraError}
              </div>
            )}
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (dniCameraSide === 'front') dniCamFrontInputRef.current?.click();
                  else dniCamBackInputRef.current?.click();
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Usar archivo
              </button>
              <button
                type="button"
                onClick={() => { stopStream(dniMediaStreamRef); setDniCameraOpen(false); }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDniCapturePhoto}
                className="inline-flex h-9 items-center rounded-sm bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88]"
              >
                Capturar {dniCameraSide === 'back' ? 'reverso' : 'anverso'}
              </button>
            </div>
          </div>
        </div>
      )}
      </CustomModal>

      {/* ── DNI: cámara (capture) y galería (sin capture) ── */}
      <input ref={dniCamFrontInputRef} type="file" accept="image/*" capture="environment" onChange={handleDniFrontCameraCapture} className="hidden" />
      <input ref={dniCamBackInputRef} type="file" accept="image/*" capture="environment" onChange={handleDniBackCameraCapture} className="hidden" />
      <input ref={dniGalleryFrontRef} type="file" accept="image/*" onChange={handleDniFrontCameraCapture} className="hidden" />
      <input ref={dniGalleryBackRef} type="file" accept="image/*" onChange={handleDniBackCameraCapture} className="hidden" />

      {/* Cambios sin guardar */}
      <ConfirmDialog
        active={askDiscard}
        title="¿Descartar los cambios?"
        description={
          id
            ? 'Los cambios de esta ficha no se han guardado. Si sales ahora se pierden.'
            : 'Lo que llevas rellenado de este socio no se ha guardado. Si sales ahora se pierde, incluidas la foto, el DNI y la firma.'
        }
        confirmLabel="Descartar cambios"
        cancelLabel="Seguir editando"
        tone="danger"
        titleId="customer-modal-discard-title"
        onConfirm={discardAndClose}
        onCancel={() => setAskDiscard(false)}
      />
    </>
  );
}

export default CustomerModal;
